import type { SymbolKey } from '@/entities/symbol';
import type { SetState } from '@/types/helpers';

import type { ExportIndexSymbol } from './symbolTransform.types';

export type CsvWidgetExportSource =
  | { sourceType: 'query'; queryFileId: string; selectedRange?: string | null }
  | { sourceType: 'datalake'; fileName: string; groupName: string };

export type CsvWidgetExportAdditionalParams = {
  type: 'source-backed-data' | 'trade-data';
  queryFileId?: string;
  fileName?: string;
  groupName?: string;
  selectedRange?: string | null;
};

export type CsvWidgetExportRequestParams = {
  id?: string;
  name: string;
  exportType: 'csv';
  source: CsvWidgetExportSource;
  from?: string | null;
  to?: string | null;
  tabName?: string;
  additionalParams: CsvWidgetExportAdditionalParams;
};

export type CsvWidgetExportRequest = {
  name: string;
  exportType: 'csv';
  exportSubType: string;
  from: string;
  to: string;
  timezone: string;
  dateFormat: string;
  includeMetadata: boolean;
  source: Extract<CsvWidgetExportSource, { sourceType: 'datalake' }>;
  tabName?: string;
  additionalParams: CsvWidgetExportAdditionalParams;
};

export enum ExportTypes {
  DATA = 'data',
  IMAGE = 'image'
}

export enum DateRangeType {
  ALL = 'all',
  RANGE = 'range',
  DATES = 'dates'
}

export interface ExportSettingsType {
  from: Date | null;
  to: Date | null;
  periodNumber: string;
  period: string;
  exportType: ExportTypes;
  exportSubType?: string;
  dateRangeType: DateRangeType;
  includeMetadata: boolean;
}

export const defaultExportSettings: ExportSettingsType = {
  from: null,
  to: null,
  periodNumber: '2',
  period: 'm',
  exportType: ExportTypes.DATA,
  dateRangeType: DateRangeType.ALL,
  includeMetadata: false
};

export type ExportRequestOverrides = {
  type: string;
  id?: string;
  exportSubType?: string | null;
  from?: string | null;
  to?: string | null;
  includeMetadata?: boolean;
  timezone?: string;
  dateFormat?: string;
  symbols?: SymbolKey[];
  group?: string;
  name?: string | null;
};

export interface ExportRequestParams {
  id?: string;
  name: string | null;
  exportType: string;
  exportSubType: string | null;
  from?: string | null;
  to?: string | null;
  timezone?: string;
  dateFormat?: string;
  includeMetadata?: boolean;
  symbols?: ExportIndexSymbol[];
}

export type ExportStatus = 'idle' | 'loading' | 'queued';

export type ExportSettingsContextType = {
  exportSettings: ExportSettingsType;
  setExportSettings: SetState<ExportSettingsType>;
  updateExportSetting: <K extends keyof ExportSettingsType>(
    key: K,
    value: ExportSettingsType[K]
  ) => void;
};

export type ExportPreviewContextType = {
  previewOptionId: string | null;
  setPreviewOptionId: (id: string | null) => void;
  previewLoading: boolean;
  setPreviewLoading: (loading: boolean) => void;
  previewHasNoData: boolean;
  setPreviewHasNoData: (hasNoData: boolean) => void;
  previewDownloadFn: (() => void) | null;
  setPreviewDownloadFn: (fn: (() => void) | null) => void;
  /**
   * Content to copy when user clicks "Copy to clipboard"
   * (e.g. Python script, Power Query, curl).
   * Falls back to exportCurlRequestString when not set.
   */
  previewContentToCopy: string | null;
  setPreviewContentToCopy: (content: string | null) => void;
};

export type ExportRequestContextType = {
  exportDataWithParams: (params: ExportRequestOverrides) => Promise<unknown>;
  loading: boolean;
  showSuccessScreen: boolean;
  setShowSuccessScreen: (show: boolean) => void;
  exportStatus: ExportStatus;
  setExportStatus: (status: ExportStatus) => void;
  resetExportStatus: () => void;
  handleCopyToClipBoard: (textToCopy: string) => void;
  handleExportData: () => Promise<unknown>;
  capturePosthogEvent: () => void;
  resetExportState: () => void;
};

export type ExportMetaContextType = {
  uniqueSymbols: Set<SymbolKey>;
  exportApiRequestString: string;
  exportCurlRequestString: string;
  timezone: string;
};

export type ExportNameContextType = {
  exportName: string | null;
  setExportName: SetState<string | null>;
};
